import type { ReactElement } from "react";

import { useSelector } from "react-redux";

import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import {
    useIsContactBeingRevealed,
    useIsContactNotRevealed,
} from "app/pages/sales-intelligence/sub-modules/common/hooks/useContactRevealStatus";
import type { IContactDetailsCellProps } from "app/pages/sales-intelligence/sub-modules/contacts-search/components/cells/ContactDetailsCell/ContactDetailsCell";
import ContactDetailsCell from "app/pages/sales-intelligence/sub-modules/contacts-search/components/cells/ContactDetailsCell/ContactDetailsCell";
import { useInvalidateContactSearchResultAsync } from "app/pages/sales-intelligence/sub-modules/contacts-search/hooks/useContactSearchResult";
import useSingleRevealContactWithUpdateQuery from "app/pages/sales-intelligence/sub-modules/contacts-search/query/queries/useContactsSingleInfoQueries";
import trackingService from "app/pages/sales-intelligence/sub-modules/contacts-search/services/tracking/trackingService";
import {
    selectContactsCost,
    selectIsActionPanelActivated,
    selectRemainingQuota,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import { selectIsContactsReadonly } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";

const ContactDetailsCellContainer = ({
    rowData,
    onApprove,
}: Pick<IContactDetailsCellProps, "rowData" | "onApprove">): ReactElement => {
    const isContactsAvailabilityReadonly = useSelector(selectIsContactsReadonly);
    const isActionPanelActivated = useSelector(selectIsActionPanelActivated);
    const remainingRevealQuota = useSelector(selectRemainingQuota);
    const contactsCost = useSelector(selectContactsCost);
    const isContactIdNotRevealed = useIsContactNotRevealed(rowData.id);
    const isContactBeingRevealed = useIsContactBeingRevealed(rowData.id);
    const invalidateContactSearchResult = useInvalidateContactSearchResultAsync();

    const mutation = useSingleRevealContactWithUpdateQuery();

    // Combined loading state includes both local reveal and reveal+export flow
    const isLoading = mutation.isPending || isContactBeingRevealed;
    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;

    const remainingRevealItems = Math.floor(remainingRevealQuota / contactsCost);
    const isNotEnoughCredits = remainingRevealItems <= 0;

    const refetchAfterTermsApproval = async () => {
        if (onApprove) {
            await onApprove();
        }
        await invalidateContactSearchResult();
    };

    function handleContactReveal() {
        if (isNotEnoughCredits && !rowData.isRevealed) {
            return;
        }

        trackingService.trackRevealContactClick();

        mutation.mutate(rowData);
    }

    return (
        <ContactDetailsCell
            isLoading={isLoading}
            isContactsAvailabilityReadonly={isContactsAvailabilityReadonly}
            isActionPanelActivated={isActionPanelActivated}
            isDataCreditsEnabled={isDataCreditsEnabled}
            remainingRevealQuota={remainingRevealQuota}
            contactsCost={contactsCost}
            isContactIdNotRevealed={isContactIdNotRevealed}
            rowData={rowData}
            onReveal={handleContactReveal}
            onApprove={refetchAfterTermsApproval}
        />
    );
};

export default ContactDetailsCellContainer;
